// components/industry-templates/MusicEventTemplate.tsx
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Music, Users, MapPin, Clock, Star, Mic2 } from 'lucide-react';

export const MusicEventTemplate = ({ structure, theme }: any) => {
  const industryData = structure.industry_data;

  return (
    <div className="space-y-8">
      {/* Artist Lineup - Hero Style */}
      {industryData?.artists && (
        <Card className="border-0 bg-gradient-to-br from-pink-500/20 to-purple-500/20 backdrop-blur overflow-hidden">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2 text-2xl">
              <Mic2 className="w-8 h-8" style={{ color: theme.primary }} />
              Artist Lineup
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Headliners */}
              {industryData.artists
                .filter((artist: any) => artist.headliner)
                .map((artist: any, idx: number) => (
                  <div
                    key={idx}
                    className="relative p-6 rounded-xl bg-gradient-to-r from-pink-500/20 to-purple-500/20 border-2"
                    style={{ borderColor: theme.primary }}
                  >
                    <div className="absolute top-4 right-4">
                      <Badge className="bg-yellow-500 text-black font-bold">
                        ⭐ HEADLINER
                      </Badge>
                    </div>
                    <div className="flex items-start gap-4">
                      <div
                        className="w-20 h-20 rounded-full flex items-center justify-center text-3xl"
                        style={{ backgroundColor: theme.primary }}
                      >
                        🎤
                      </div>
                      <div className="flex-1">
                        <h3 className="text-3xl font-bold text-white mb-2">{artist.name}</h3>
                        <div className="flex gap-3 flex-wrap">
                          <Badge className="text-sm" style={{ backgroundColor: theme.primary }}>
                            {artist.genre}
                          </Badge>
                          {artist.performance_time && (
                            <Badge variant="outline" className="text-sm border-white text-white">
                              <Clock className="w-3 h-3 mr-1" />
                              {new Date(artist.performance_time).toLocaleString('en-US', {
                                weekday: 'short',
                                hour: '2-digit',
                                minute: '2-digit',
                              })}
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

              {/* Regular Artists Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                {industryData.artists
                  .filter((artist: any) => !artist.headliner)
                  .map((artist: any, idx: number) => (
                    <div
                      key={idx}
                      className="flex gap-3 p-4 rounded-lg bg-white/5 border border-white/10 hover:border-pink-500/50 transition-all hover:scale-105"
                    >
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-xl"
                        style={{ backgroundColor: theme.primary }}
                      >
                        🎵
                      </div>
                      <div className="flex-1">
                        <h4 className="font-bold text-white">{artist.name}</h4>
                        <p className="text-sm text-slate-400">{artist.genre}</p>
                        {artist.performance_time && (
                          <p className="text-xs text-slate-500 mt-1">
                            {new Date(artist.performance_time).toLocaleString('en-US', {
                              hour: '2-digit',
                              minute: '2-digit',
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Stages Info */}
      {industryData?.stages && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <MapPin className="w-6 h-6" style={{ color: theme.primary }} />
              Festival Stages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {industryData.stages.map((stage: any, idx: number) => (
                <div
                  key={idx}
                  className="p-5 rounded-lg bg-gradient-to-br from-pink-500/10 to-purple-500/10 border border-white/10"
                >
                  <div className="text-3xl mb-3">🎪</div>
                  <h4 className="font-bold text-white text-lg mb-2">{stage.name}</h4>
                  <p className="text-sm text-slate-400 mb-3">{stage.description}</p>
                  <Badge style={{ backgroundColor: theme.primary }}>
                    Capacity: {stage.capacity}
                  </Badge>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Food Vendors */}
      {industryData?.food_vendors && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              🍔 Food & Beverages
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {industryData.food_vendors.map((vendor: string, idx: number) => (
                <div
                  key={idx}
                  className="p-3 text-center rounded-lg bg-white/5 border border-white/10 hover:bg-white/10 transition-colors"
                >
                  <p className="text-white font-medium text-sm">{vendor}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Merchandise */}
      {industryData?.merchandise_items && (
        <Card className="border border-white/10 bg-slate-900/50 backdrop-blur">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              👕 Official Merchandise
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-2">
              {industryData.merchandise_items.map((item: string, idx: number) => (
                <Badge
                  key={idx}
                  variant="outline"
                  className="border-white/20 text-white text-sm py-2 px-4"
                >
                  {item}
                </Badge>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
